import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BudgetDialog({ open, onOpenChange, budget, categories, currency, onSaved }) {
  const [formData, setFormData] = useState({
    category: '',
    limit_amount: '',
    period: 'monthly'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (budget) {
      setFormData({
        category: budget.category,
        limit_amount: budget.limit_amount.toString(),
        period: budget.period || 'monthly'
      });
    } else {
      setFormData({
        category: '',
        limit_amount: '',
        period: 'monthly'
      });
    }
  }, [budget, open]);

  const expenseCategories = categories.filter(c => c.type === 'expense');

  const getSubmitLabel = () => {
    if (loading) return 'Saving...';
    if (budget) return 'Update';
    return 'Add';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category || !formData.limit_amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    const limitNum = parseFloat(formData.limit_amount);
    if (!(limitNum > 0)) {
      toast.error('Limit must be greater than 0');
      return;
    }

    setLoading(true);
    try {
      const url = budget
        ? `${API}/budgets/${budget.budget_id}`
        : `${API}/budgets`;
      const method = budget ? 'PUT' : 'POST';

      const body = budget
        ? { limit_amount: limitNum, period: formData.period }
        : {
            category: formData.category,
            limit_amount: limitNum,
            currency: currency,
            period: formData.period
          };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(budget ? 'Budget updated' : 'Budget created');
        onSaved();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save budget');
      }
    } catch {
      toast.error('Failed to save budget');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl tracking-tight font-semibold">
            {budget ? 'Edit Budget Goal' : 'Add Budget Goal'}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Set a spending limit for a category in {currency}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="budget-category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
                disabled={!!budget}
              >
                <SelectTrigger data-testid="budget-category-select" id="budget-category">
                  <SelectValue placeholder="Select an expense category" />
                </SelectTrigger>
                <SelectContent>
                  {expenseCategories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="budget-limit">Limit ({currency}) *</Label>
              <Input
                data-testid="budget-limit-input"
                id="budget-limit"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={formData.limit_amount}
                onChange={(e) => setFormData({ ...formData, limit_amount: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="budget-period">Period *</Label>
              <Select
                value={formData.period}
                onValueChange={(value) => setFormData({ ...formData, period: value })}
              >
                <SelectTrigger data-testid="budget-period-select" id="budget-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              data-testid="save-budget-button"
              type="submit"
              disabled={loading}
              className="transition-transform duration-200 hover:-translate-y-px"
            >
              {getSubmitLabel()}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default BudgetDialog;
